import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import type { AcceptanceLetterTemplateVariant } from './acceptanceLetterTemplates';

const PAGE_WIDTH = 612;
const PAGE_HEIGHT = 792;
const BODY_X = 72;
const BODY_MAX_WIDTH = 504;
const BODY_FONT_SIZE = 11;
const LINE_HEIGHT = 15.44;
const PARAGRAPH_GAP = 9;

const templatePdfUrlByVariant: Record<AcceptanceLetterTemplateVariant, string> = {
  'initial-all': new URL('../assets/acceptance-letter/template-initial-all.pdf', import.meta.url).toString(),
  'complete-boys': new URL('../assets/acceptance-letter/template-complete-boys.pdf', import.meta.url).toString(),
  'complete-girls': new URL('../assets/acceptance-letter/template-complete-girls.pdf', import.meta.url).toString(),
};

const signatureUrlByVariant: Record<AcceptanceLetterTemplateVariant, string> = {
  'initial-all': new URL('../assets/acceptance-letter/signature-initial-all.jpg', import.meta.url).toString(),
  'complete-boys': new URL('../assets/acceptance-letter/signature-complete-boys.jpg', import.meta.url).toString(),
  'complete-girls': new URL('../assets/acceptance-letter/signature-complete-girls.jpg', import.meta.url).toString(),
};

const signatureTargetWidthByVariant: Record<AcceptanceLetterTemplateVariant, number> = {
  'initial-all': 102,
  'complete-boys': 154,
  'complete-girls': 118,
};

const assetBytesCache = new Map<string, Uint8Array>();

const loadAssetBytes = async (url: string): Promise<Uint8Array> => {
  const cached = assetBytesCache.get(url);
  if (cached) return cached;
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to load acceptance letter asset (${response.status}) from ${url}`);
  }
  const bytes = new Uint8Array(await response.arrayBuffer());
  assetBytesCache.set(url, bytes);
  return bytes;
};

const normalizeBody = (value: string): string =>
  String(value ?? '')
    .replace(/\r\n/g, '\n')
    .replace(/\uFEFF/g, '')
    .trim();

const toParagraphs = (value: string): string[] =>
  normalizeBody(value)
    .split(/\n{2,}/)
    .map((entry) => entry.trim())
    .filter(Boolean);

const wrapLine = (
  text: string,
  maxWidth: number,
  measure: (value: string) => number
): string[] => {
  const tokens = text.split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return [''];
  const lines: string[] = [];
  let current = '';
  for (const token of tokens) {
    const proposal = current ? `${current} ${token}` : token;
    if (measure(proposal) <= maxWidth) {
      current = proposal;
      continue;
    }
    if (current) {
      lines.push(current);
      current = token;
      continue;
    }
    lines.push(token);
  }
  if (current) lines.push(current);
  return lines;
};

const drawLineAtTop = (input: {
  page: ReturnType<PDFDocument['addPage']>;
  text: string;
  top: number;
  font: Awaited<ReturnType<PDFDocument['embedFont']>>;
  size?: number;
}) => {
  const size = input.size ?? BODY_FONT_SIZE;
  input.page.drawText(input.text, {
    x: BODY_X,
    y: PAGE_HEIGHT - input.top - size,
    size,
    font: input.font,
    color: rgb(0.08, 0.09, 0.11),
  });
};

const drawWrappedParagraphAtTop = (input: {
  page: ReturnType<PDFDocument['addPage']>;
  paragraph: string;
  top: number;
  font: Awaited<ReturnType<PDFDocument['embedFont']>>;
}): number => {
  const lines = wrapLine(
    input.paragraph,
    BODY_MAX_WIDTH,
    (value) => input.font.widthOfTextAtSize(value, BODY_FONT_SIZE)
  );
  let cursorTop = input.top;
  for (const line of lines) {
    drawLineAtTop({
      page: input.page,
      text: line,
      top: cursorTop,
      font: input.font,
      size: BODY_FONT_SIZE,
    });
    cursorTop += LINE_HEIGHT;
  }
  return cursorTop;
};

const toSignatureBlock = (paragraph: string): { salutation: string; names: string[] } => {
  const lines = paragraph
    .split('\n')
    .map((entry) => entry.trim())
    .filter(Boolean);
  const salutation = lines[0] ?? 'Sincerely,';
  const names = lines.slice(1);
  return { salutation, names };
};

export const buildAcceptanceLetterPdf = async (input: {
  variant: AcceptanceLetterTemplateVariant;
  body: string;
  title?: string;
}): Promise<Uint8Array> => {
  const paragraphs = toParagraphs(input.body);
  if (paragraphs.length === 0) {
    throw new Error('Acceptance letter body is empty.');
  }

  const templatePdfBytes = await loadAssetBytes(templatePdfUrlByVariant[input.variant]);
  const signatureBytes = await loadAssetBytes(signatureUrlByVariant[input.variant]);

  const templateDoc = await PDFDocument.load(templatePdfBytes);
  const pdf = await PDFDocument.create();
  const [templatePage] = await pdf.copyPages(templateDoc, [0]);
  const page = pdf.addPage(templatePage);
  const regular = await pdf.embedFont(StandardFonts.Helvetica);

  // Erase dynamic-content region from the static template and redraw with live values.
  page.drawRectangle({
    x: 66,
    y: PAGE_HEIGHT - 186 - 430,
    width: 520,
    height: 430,
    color: rgb(1, 1, 1),
  });

  const dateLine = paragraphs[0];
  drawLineAtTop({
    page,
    text: dateLine,
    top: 199,
    font: regular,
    size: BODY_FONT_SIZE,
  });

  let cursorTop = 253;
  for (const paragraph of paragraphs.slice(1)) {
    const isSignatureParagraph = /^sincerely[, ]*$/i.test(paragraph.split('\n')[0]?.trim() ?? '');
    if (isSignatureParagraph) {
      const signature = toSignatureBlock(paragraph);
      cursorTop = drawWrappedParagraphAtTop({
        page,
        paragraph: signature.salutation,
        top: cursorTop,
        font: regular,
      });
      cursorTop += 22;

      const signatureImage = await pdf.embedJpg(signatureBytes);
      const targetWidth = signatureTargetWidthByVariant[input.variant];
      const targetHeight = signatureImage.height * (targetWidth / signatureImage.width);
      page.drawImage(signatureImage, {
        x: BODY_X,
        y: PAGE_HEIGHT - cursorTop - targetHeight,
        width: targetWidth,
        height: targetHeight,
      });

      cursorTop += targetHeight + 20;
      for (const line of signature.names) {
        cursorTop = drawWrappedParagraphAtTop({
          page,
          paragraph: line,
          top: cursorTop,
          font: regular,
        });
        cursorTop += 3;
      }
      continue;
    }

    cursorTop = drawWrappedParagraphAtTop({
      page,
      paragraph,
      top: cursorTop,
      font: regular,
    });
    cursorTop += PARAGRAPH_GAP;
  }

  return pdf.save();
};
